import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { chunk, mapValues } from "lodash";
import {
  ActionIcon,
  Button,
  Group,
  Pagination,
  Switch,
  Stack,
  SimpleGrid,
} from "@mantine/core";
import { Refresh, ScreenShare } from "tabler-icons-react";

import { getImageUrl } from "common/utils/getImageUrl";
import { useOrganization } from "common/admin/auth/hooks";
import QueryWrapper from "common/components/QueryWrapper";
import type { Message } from "wall/types/message.model";
import { useWall } from "wall/hooks/useWall";
import {
  useGetPresignedXMLConfigPostMutation,
  useUploadToS3Mutation,
} from "common/upload/api/upload.enpoints";
import {
  useListMessagesQuery,
  useUpdateMessageMutation,
} from "wall/api/messages.endpoints";
import MessageCard from "./MessageCard";
import MessageStateSelect from "wall/admin/wall/messages/MessageStateSelect";
import { MessagesEditModal } from "wall/admin/wall/messages/MessagesEditModal";
import { openWindow } from "common/utils/openWindow";
import { getWallCastUrl } from "common/utils/routeHelpers";
import { config, ScreenType } from "config";

import { Wall } from "wall/types/wall.model";

import { WallService } from "services/wall.service";

export type MessageState = "all" | "rejected" | "approved" | "unset";

const MESSAGES_DISPLAYED = 20;

interface MessageGridProps {
  wallId: string;
  handleTogglePlayPause: () => Promise<any>;
  isSlideshowLoading: boolean;
  form: any;
  // other properties...
}

const MessageGrid = ({
  wallId,
  handleTogglePlayPause,
  isSlideshowLoading,
  form,
}: MessageGridProps) => {
  const [organization] = useOrganization();
  const wall = useWall()!;
  const [needRefresh, setNeedRefresh] = useState<boolean>(false);
  const listMessagesQuery = useListMessagesQuery({ wallId: wall.id });
  const [stateFilter, setStateFilter] = useState<MessageState>("all");
  const [isEditingModalOpen, setIsEditingModalOpen] = useState(false);
  const [editedMessages, setEditedMessages] = useState<Message[]>([]);
  const [uploadURL, setUploadURL] = useState<string | null>(null);
  const [resetSelection, setResetSelection] = useState(false);
  const [wallPolled, setWallPolled] = useState<Partial<Wall> | null>(null);

  const [getPresignedPost, getPresignedPostResult] =
    useGetPresignedXMLConfigPostMutation();
  const [uploadToS3, uploadToS3Result] = useUploadToS3Mutation();
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const POLLING_INTERVAL = 1000; // 10 seconds
  const fetchWallData = useCallback(async () => {
    try {
      const wallService = new WallService();
      const data = await wallService.getWallById({ wallId: wall.id });
      console.log("Fetched wall data:", data);

      const newWall = data as unknown as Wall;
      setWallPolled(newWall);
    } catch (error) {
      console.error("Error fetching wall data:", error);
    }
  }, [wall.id]);

  // Combined polling function
  const pollData = useCallback(async () => {
    await fetchWallData(); // Then fetch wall data
  }, [fetchWallData]);

  useEffect(() => {
    // Fetch immediately on mount
    pollData();

    // Set up polling interval
    pollingIntervalRef.current = setInterval(pollData, POLLING_INTERVAL);

    // Cleanup polling interval
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [pollData]);

  const messagesByState = useMemo(() => {
    const messages = listMessagesQuery.data || [];
    const msgApproved = messages.filter(({ approved }) => approved);
    const msgRejected = messages.filter(({ approved }) => approved === false);
    const msgUnset = messages.filter(({ approved }) => approved === undefined);
    return {
      all: [...messages],
      approved: msgApproved,
      rejected: msgRejected,
      unset: msgUnset,
    };
  }, [listMessagesQuery.data]);

  const pages = useMemo(
    () => chunk(messagesByState[stateFilter], MESSAGES_DISPLAYED),
    [messagesByState, stateFilter],
  );

  const [pageIndex, setPageIndex] = useState(0);

  const organizationName = useMemo(
    () => (wall.organization ? wall.organization : organization),
    [organization, wall],
  );

  const screenType = useMemo<ScreenType | undefined>(
    () => (wall ? wall.template : undefined),
    [wall],
  );

  const displayedWidth = useMemo<number | undefined>(
    () =>
      screenType &&
      config.screenSizes[screenType] &&
      config.screenSizes[screenType].length > 0
        ? config.screenSizes[screenType][0]
        : undefined,
    [screenType],
  );
  const displayedHeight = useMemo<number | undefined>(
    () =>
      screenType &&
      config.screenSizes[screenType] &&
      config.screenSizes[screenType].length > 0
        ? config.screenSizes[screenType][1]
        : undefined,
    [screenType],
  );

  const handleApprovedMessagesLog = useCallback(() => {
    const approvedMessages = messagesByState.approved;
    if (approvedMessages.length > 0) {
      setEditedMessages(approvedMessages);
      setIsEditingModalOpen(true);
    }
  }, [messagesByState]);

  useEffect(() => {
    setPageIndex(0);
  }, [stateFilter]);

  const submit = async () => {
    const xml: XMLDocument = document.implementation.createDocument(null, null);
    xml.append(
      xml.createProcessingInstruction("xml", 'version="1.0" encoding="UTF-8"'),
    );
    const messages = xml.createElement("Questions");
    xml.append(messages);

    for (const message of editedMessages) {
      const messageElement = xml.createElement("Question");

      const nick = xml.createElement("Pseudo");
      nick.textContent = message.author;
      messageElement.append(nick);

      const text = xml.createElement("Texte");
      text.textContent = message.message;
      messageElement.append(text);

      if (
        message.imageKey &&
        (message.imageKeyApproved === true ||
          message.imageKeyApproved === undefined)
      ) {
        const pic = xml.createElement("image");
        pic.textContent = getImageUrl(message.imageKey);
        messageElement.append(pic);
      }

      messages.append(messageElement);
    }

    const stem =
      organizationName === "Nanterre"
        ? `MESSAGES-${organizationName.toUpperCase()}`
        : `MESSAGES-${wallId}`;

    const filename = `${stem}.xml`;
    const file = new File(
      [new XMLSerializer().serializeToString(xml)],
      filename,
    );

    try {
      const data = await getPresignedPost({ filename: stem }).unwrap();
      await uploadToS3({ file, data });
    } catch (error) {
      console.log(error);
      return;
    }
    setUploadURL(getImageUrl(filename));
  };

  const messages = useMemo(
    () => (listMessagesQuery.data ? listMessagesQuery.data : []),
    [listMessagesQuery.data],
  );

  useEffect(() => {
    if (needRefresh) {
      listMessagesQuery.refetch();
      setNeedRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needRefresh]);

  const [updateMessage, { isLoading }] = useUpdateMessageMutation();

  useEffect(() => {
    if (!resetSelection) return;
    if (resetSelection && messagesByState.approved.length > 0) {
      messagesByState.approved.map(updateApprovedMessage);
      setResetSelection(false);
    }

    async function updateApprovedMessage(msg: Message) {
      return await updateMessage({ ...msg, approved: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSelection, messagesByState.approved]);

  const handleTogglePlayPauseWithUpdate = useCallback(async () => {
    try {
      const updatedWall = await handleTogglePlayPause();
      if (updatedWall) {
        // Immediately update the wallPolled state with the response from the API
        setWallPolled(updatedWall);
      }
    } catch (error) {
      console.error("Error toggling play/pause:", error);
    }
  }, [handleTogglePlayPause]);

  return (
    <>
      <QueryWrapper query={listMessagesQuery}>
        <Stack>
          <Group position={"apart"}>
            <Group spacing={5} position={"right"}>
              <MessageStateSelect
                value={stateFilter}
                onChange={setStateFilter}
                counts={mapValues(messagesByState, (m) => m.length)}
              />
              <ActionIcon
                variant={"default"}
                size={36}
                onClick={listMessagesQuery.refetch}
              >
                <Refresh size={18} />
              </ActionIcon>
            </Group>
            <Group spacing={4} position={"right"} className="flex">
              <span>Reset Sélection</span>
              <Switch
                checked={resetSelection}
                className="flex"
                onChange={(event) =>
                  setResetSelection(event.currentTarget.checked)
                }
              />
            </Group>
            <Pagination
              position={"right"}
              page={pageIndex + 1}
              onChange={(page) => setPageIndex(page - 1)}
              total={pages.length}
            />
          </Group>
          <Group position="apart">
            <Group>
              <Button
                className="bg-button-admin disabled:opacity-75"
                loading={isSlideshowLoading}
                onClick={handleTogglePlayPauseWithUpdate}
              >
                {wallPolled?.slideshow?.status === "PLAY"
                  ? "Stop Slideshow"
                  : "Start Slideshow"}
              </Button>

              <div>{`${wallPolled?.slideshow?.currentSlideshowIndex! + 1}/${wallPolled?.slideshow?.totalMessages}`}</div>
            </Group>
            <Button
              leftIcon={<ScreenShare size={14} />}
              variant="outline"
              className="border-none"
              onClick={() =>
                openWindow(
                  getWallCastUrl(wall.id),
                  displayedWidth,
                  displayedHeight,
                )
              }
            >
              Open screen cast
            </Button>
          </Group>
          {messages.length > 0 && (
            <Stack>
              {/* <LoadingOverlay visible={isLoading} overlayBlur={2} /> */}
              <SimpleGrid
                cols={3}
                breakpoints={[
                  { maxWidth: "lg", cols: 3, spacing: "md" },
                  { maxWidth: "md", cols: 2, spacing: "md" },
                  { maxWidth: "sm", cols: 1, spacing: "sm" },
                ]}
              >
                {!isLoading
                  ? (pages[pageIndex] || []).map((message, indice) => {
                      const getMessageStart = () => {
                        const iterationIndice = indice + 1;
                        return pageIndex * MESSAGES_DISPLAYED + iterationIndice;
                      };
                      return (
                        <MessageCard
                          messageStart={getMessageStart()}
                          message={message}
                          key={message.id}
                          onClick={() => setNeedRefresh(true)}
                        />
                      );
                    })
                  : null}
              </SimpleGrid>
              <Pagination
                position={"right"}
                page={pageIndex + 1}
                onChange={(page) => setPageIndex(page - 1)}
                total={pages.length}
              />
              <Group position="right">
                <Button
                  className="bg-button-admin"
                  onClick={handleApprovedMessagesLog}
                  style={{ marginTop: "10px" }}
                >
                  Generate XML
                </Button>
              </Group>
            </Stack>
          )}
        </Stack>
      </QueryWrapper>
      {isEditingModalOpen && (
        <MessagesEditModal
          uploadURL={uploadURL}
          setUploadURL={setUploadURL}
          isEditingModalOpen={isEditingModalOpen}
          setIsEditingModalOpen={setIsEditingModalOpen}
          getPresignedPostResult={getPresignedPostResult}
          uploadToS3Result={uploadToS3Result}
          editedMessages={editedMessages}
          setEditedMessages={setEditedMessages}
          onSubmit={submit}
        />
      )}
    </>
  );
};

export { MessageGrid };
